/**
 * Utility functions to help interpret our current feature flags with respect to a user's current feature flag state
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Kelvin Nguyen
 */

/**
 * Returns a boolean that specifies whether to show a feature based on the feature flag options
 * Use this method if you want to enable "pessmistic" feature loading (i.e. only features that have been properly activated should be shown)
 * @param {Object} options The feature flag options
 */
const shouldShowFeature = (options = {}) => {
  const { featureFlags = {}, userFeatureFlags = {}, featureName, flagName } = options

  if (featureName && flagName) {
    const featureFlag = (featureFlags[featureName])
      ? (featureFlags[featureName][flagName] || {})
      : {}
    const userFeatureFlag = (userFeatureFlags[featureName])
      ? (userFeatureFlags[featureName][flagName] || {})
      : {}

    if (featureFlag.enabled && (featureFlag.enabled !== userFeatureFlag.enabled)) {
      return true
    }
  }

  return false
}

/**
 * Goes through the list of flag names and checks whether all the specified flags have been enabled for the user
 * @param {Object} userFeatureFlags The current user's feature flags
 * @param {String} featureName The current feature name
 * @param {Array} flagNames The list of flag names to check against the feature
 */
const userFlagsEnabled = (userFeatureFlags = {}, featureName, flagNames = []) => {
  return flagNames.every((flagName) => {
    return isUserFlagEnabled(userFeatureFlags, featureName, flagName)
  })
}

/**
 * Returns a boolean that indicates whether the current flag has been triggered as 'enabled' for a user
 * Use this method if you want to enable "optimistc" feature loading (i.e. if the user never took any action to change their preferences, always enable the featureit)
 * @param {Object} userFeatureFlags The current user's feature flags
 * @param {String} featureName The current feature name
 * @param {String} flagName The current flag name
 */
const isUserFlagEnabled = (userFeatureFlags = {}, featureName, flagName) => {
  if (userFeatureFlags[featureName] && userFeatureFlags[featureName][flagName]) {
    return userFeatureFlags[featureName][flagName].enabled
  }

  return false
}

export default {
  shouldShowFeature,
  isUserFlagEnabled,
  userFlagsEnabled
}
